import React, { useState, useEffect, useCallback } from 'react';
import { Phone, Building2, User, Globe, X, Save, Loader2, ExternalLink, Filter, ChevronRight, PhoneIncoming, PhoneOutgoing, Mic } from 'lucide-react';
import { supabase } from '../services/supabaseService';
import {
  listCallsForProspect,
  countCallsByProspect,
  OutboundCall,
  ProspectCallSummary,
} from '../services/zadarmaService';

// --- Types ---

interface OutboundProspect {
  id: string;
  company_name: string;  // mapped from 'name' in JSON
  website: string;
  province: string;
  phone: string;
  company_email: string;
  size_estimate: string;
  employees_approx: number | null;
  employees_range: string;
  segment: string;
  scope: string;
  score: number | null;
  tier: string | null;
  pain_signals: string[];
  buy_signals: string[];
  notes: string;
  needs_call: boolean;
  call_status: string;
  call_questions: string[];
  call_notes: string | null;
  decision_maker_name: string | null;
  decision_maker_role: string | null;
  decision_maker_email: string | null;
  excluded: boolean;
  exclusion_reason: string | null;
  is_public: boolean;
  multi_site: boolean;
  family_historic: boolean;
  public_contract: boolean;
  size_confirmed_at: string | null;
}

type CallStatusFilter = 'pendiente' | 'no_contesta' | 'llamada_hecha';

const SIZE_RANGES = [
  { label: '<50 empleados', value: '<50' },
  { label: '50-100', value: '50-100' },
  { label: '100-300', value: '100-300' },
  { label: '300-500', value: '300-500' },
  { label: '501-1000', value: '501-1000' },
  { label: '>1000', value: '>1000' },
];

const RESULT_OPTIONS = [
  { label: 'Llamada hecha', value: 'llamada_hecha' },
  { label: 'No contesta', value: 'no_contesta' },
  { label: 'Descartado', value: 'descartado' },
];

// --- Scoring ---

function computeScore(prospect: OutboundProspect, sizeRange: string, dmName: string, dmEmail: string): { score: number; tier: string } {
  let score = 0;

  // +30 sector ICP (all are ambulancias)
  score += 30;

  // Size scoring based on confirmed range
  if (sizeRange === '50-100' || sizeRange === '100-300') {
    score += 20;
  } else if (sizeRange === '300-500') {
    score += 10;
  } else if (sizeRange === '501-1000' || sizeRange === '>1000') {
    score -= 10;
  }
  // <50 → 0

  // Decision maker
  if (dmName && dmName.trim()) {
    score += 20;
  }
  if (dmEmail && dmEmail.trim() && dmEmail.includes('@')) {
    score += 5;
  }

  // Pain signals
  const painCount = Array.isArray(prospect.pain_signals) ? prospect.pain_signals.length : 0;
  score += Math.min(painCount * 10, 30);

  // Buy signals
  const buyCount = Array.isArray(prospect.buy_signals) ? prospect.buy_signals.length : 0;
  score += Math.min(buyCount * 10, 20);

  // Boolean flags
  if (prospect.multi_site) score += 10;
  if (prospect.family_historic) score += 10;
  if (prospect.public_contract) score += 10;
  if (prospect.is_public) score -= 30;

  // Tier
  let tier = 'D';
  if (score >= 75) tier = 'A';
  else if (score >= 50) tier = 'B';
  else if (score >= 25) tier = 'C';

  return { score, tier };
}

// --- Component ---

function formatCallDuration(seconds: number | null): string {
  if (seconds == null || seconds < 0) return '—';
  const mm = Math.floor(seconds / 60);
  const ss = seconds % 60;
  return `${mm}:${ss.toString().padStart(2, '0')}`;
}

const OutboundCallQueue: React.FC = () => {
  const [prospects, setProspects] = useState<OutboundProspect[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<CallStatusFilter>('pendiente');
  const [selectedProspect, setSelectedProspect] = useState<OutboundProspect | null>(null);
  const [saving, setSaving] = useState(false);
  const [callSummaries, setCallSummaries] = useState<Record<string, ProspectCallSummary>>({});
  const [drawerCalls, setDrawerCalls] = useState<OutboundCall[]>([]);
  const [drawerCallsLoading, setDrawerCallsLoading] = useState(false);

  // Form state
  const [formSizeRange, setFormSizeRange] = useState('');
  const [formSizeComment, setFormSizeComment] = useState('');
  const [formDMName, setFormDMName] = useState('');
  const [formDMRole, setFormDMRole] = useState('');
  const [formDMEmail, setFormDMEmail] = useState('');
  const [formCallNotes, setFormCallNotes] = useState('');
  const [formResult, setFormResult] = useState('');
  const [formDiscardReason, setFormDiscardReason] = useState('');

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('outbound_prospects')
      .select('*')
      .eq('needs_call', true)
      .eq('call_status', statusFilter)
      .order('score', { ascending: false, nullsFirst: false });

    if (error) {
      console.error('Error fetching outbound_prospects:', error);
    } else {
      // call_questions is stored as a JSON-encoded TEXT in BD, not jsonb.
      // Parse to array so render-side Array.isArray() works.
      const normalized = (data || []).map((row: Record<string, unknown>) => {
        const raw = row.call_questions;
        let parsed: string[] = [];
        if (Array.isArray(raw)) {
          parsed = raw as string[];
        } else if (typeof raw === 'string' && raw.trim().startsWith('[')) {
          try {
            const candidate = JSON.parse(raw);
            if (Array.isArray(candidate)) parsed = candidate;
          } catch {
            // leave parsed as []
          }
        }
        return { ...row, call_questions: parsed };
      });
      const typed = normalized as OutboundProspect[];
      setProspects(typed);
      const ids = typed.map((p) => p.id).filter(Boolean);
      const summaries = await countCallsByProspect(ids);
      setCallSummaries(summaries);
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => {
    fetchProspects();
  }, [fetchProspects]);

  useEffect(() => {
    if (!selectedProspect) {
      setDrawerCalls([]);
      return;
    }
    let cancelled = false;
    setDrawerCallsLoading(true);
    listCallsForProspect(selectedProspect.id).then((rows) => {
      if (cancelled) return;
      setDrawerCalls(rows);
      setDrawerCallsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [selectedProspect]);

  const openPanel = (prospect: OutboundProspect) => {
    setSelectedProspect(prospect);
    // Pre-fill form with existing data
    setFormSizeRange(prospect.employees_range || '');
    setFormSizeComment('');
    setFormDMName(prospect.decision_maker_name || '');
    setFormDMRole(prospect.decision_maker_role || '');
    setFormDMEmail(prospect.decision_maker_email || '');
    setFormCallNotes(prospect.call_notes || '');
    setFormResult('');
    setFormDiscardReason('');
  };

  const closePanel = () => {
    setSelectedProspect(null);
  };

  const handleSave = async () => {
    if (!selectedProspect) return;
    if (!formResult) return;
    if (formResult === 'descartado' && !formDiscardReason.trim()) return;

    setSaving(true);

    // Build size_estimate text from the range
    const sizeLabel = SIZE_RANGES.find(r => r.value === formSizeRange)?.label || formSizeRange;
    const sizeEstimateText = formSizeComment
      ? `${sizeLabel} (confirmado por llamada: ${formSizeComment})`
      : `${sizeLabel} (confirmado por llamada)`;

    // Check if excluded due to size
    const isExcludedBySize = formSizeRange === '501-1000' || formSizeRange === '>1000';

    // Compute new score
    const { score: newScore, tier: newTier } = computeScore(
      selectedProspect,
      formSizeRange,
      formDMName,
      formDMEmail
    );

    const updatePayload: Record<string, unknown> = {
      size_estimate: sizeEstimateText,
      employees_range: formSizeRange,
      decision_maker_name: formDMName || null,
      decision_maker_role: formDMRole || null,
      decision_maker_email: formDMEmail || null,
      call_notes: formCallNotes || null,
      call_status: formResult,
      size_confirmed_at: new Date().toISOString(),
      score: newScore,
      tier: newTier,
    };

    if (isExcludedBySize) {
      updatePayload.excluded = true;
      updatePayload.exclusion_reason = 'Tamaño >500 confirmado por llamada';
      updatePayload.needs_call = false;
    }

    if (formResult === 'descartado') {
      updatePayload.excluded = true;
      updatePayload.exclusion_reason = formDiscardReason;
      updatePayload.needs_call = false;
    }

    if (formResult === 'llamada_hecha') {
      updatePayload.needs_call = false;
    }

    const { error } = await supabase
      .from('outbound_prospects')
      .update(updatePayload)
      .eq('id', selectedProspect.id);

    if (error) {
      console.error('Error updating prospect:', error);
      alert('Error al guardar: ' + error.message);
      setSaving(false);
      return;
    }

    // Cerrar el loop outbound → CRM cuando la llamada tuvo éxito con un
    // decisor identificado y el prospect no está excluido. Si ya existe un
    // lead vinculado (crm_lead_id != null) no duplicamos. La columna
    // leads.outbound_prospect_id sirve para reporting cruzado del funnel.
    const shouldPromoteToLead =
      formResult === 'llamada_hecha' &&
      !isExcludedBySize &&
      (formDMEmail.trim() || formDMName.trim()) &&
      !selectedProspect.crm_lead_id;

    if (shouldPromoteToLead) {
      const leadPayload = {
        full_name: formDMName.trim() || selectedProspect.company_name || 'Sin nombre',
        email: formDMEmail.trim() || `${selectedProspect.id}@no-email.outbound`,
        phone: (selectedProspect as any).phone || null,
        company: selectedProspect.company_name || '',
        role: formDMRole.trim() || null,
        source: 'phone_call',
        status: 'contactado',
        owner: 'outbound',
        notes: formCallNotes || '',
        vertical: (selectedProspect as any).vertical || null,
        outbound_prospect_id: selectedProspect.id,
        score: newScore,
      };

      const { data: newLead, error: leadError } = await supabase
        .from('leads')
        .insert(leadPayload)
        .select('id')
        .single();

      if (leadError) {
        console.warn('No se pudo promover prospect a lead:', leadError);
      } else if (newLead?.id) {
        await supabase
          .from('outbound_prospects')
          .update({ crm_lead_id: newLead.id })
          .eq('id', selectedProspect.id);
      }
    }

    closePanel();
    fetchProspects();
    setSaving(false);
  };

  const tierColor = (tier: string | null) => {
    switch (tier) {
      case 'A': return 'bg-green-100 text-green-800';
      case 'B': return 'bg-blue-100 text-blue-800';
      case 'C': return 'bg-yellow-100 text-yellow-800';
      case 'D': return 'bg-gray-100 text-gray-600';
      default: return 'bg-gray-100 text-gray-500';
    }
  };

  const statusLabel = (s: CallStatusFilter) => {
    switch (s) {
      case 'pendiente': return 'Pendientes';
      case 'no_contesta': return 'No contesta';
      case 'llamada_hecha': return 'Llamada hecha';
    }
  };

  const statusCount = (s: CallStatusFilter) => {
    // This is the count for the currently loaded filter; we show a badge only for 'pendiente'
    if (s === statusFilter) return prospects.length;
    return null;
  };

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Phone className="w-6 h-6 text-blue-600" />
          Prospección — Pendientes de llamada
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Llama para confirmar tamaño y encontrar al decisor
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 mb-4">
        <Filter className="w-4 h-4 text-gray-400" />
        {(['pendiente', 'no_contesta', 'llamada_hecha'] as CallStatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === s
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {statusLabel(s)}
            {statusFilter === s && prospects.length > 0 && (
              <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-xs">
                {prospects.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-500">Cargando prospectos...</span>
        </div>
      ) : prospects.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Phone className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>No hay prospectos con estado "{statusLabel(statusFilter)}"</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Empresa</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Provincia</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Teléfono</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tier</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Llamadas</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Preguntas</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {prospects.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => openPanel(p)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{p.company_name || p.notes?.slice(0, 30)}</div>
                      {p.website && (
                        <div className="text-xs text-gray-400">{p.website}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{p.province || '—'}</td>
                    <td className="px-4 py-3">
                      {p.phone ? (
                        <a
                          href={`tel:${p.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                        >
                          <Phone className="w-3.5 h-3.5" />
                          {p.phone}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">Sin teléfono</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.tier && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${tierColor(p.tier)}`}>
                          {p.tier} ({p.score})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const s = callSummaries[p.id];
                        if (!s || s.count === 0) {
                          return <span className="text-xs text-gray-300">—</span>;
                        }
                        return (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                              {s.count}
                            </span>
                            {s.hasAudio && <Mic className="w-3.5 h-3.5 text-gray-500" />}
                            {s.lastStatus && (
                              <span className="text-xs text-gray-500 truncate max-w-[6rem]" title={s.lastStatus}>
                                {s.lastStatus}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      {Array.isArray(p.call_questions) && p.call_questions.length > 0 ? (
                        <div className="text-xs text-gray-500 space-y-0.5">
                          {p.call_questions.map((q, i) => (
                            <div key={i} className="truncate max-w-xs">
                              {i + 1}. {q}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Slide-over panel */}
      {selectedProspect && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/30 z-40 transition-opacity"
            onClick={closePanel}
          />

          {/* Panel */}
          <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
            {/* Panel header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
              <div className="min-w-0">
                <h2 className="text-lg font-bold text-gray-900 truncate flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  {selectedProspect.company_name}
                </h2>
                <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                  {selectedProspect.province && <span>{selectedProspect.province}</span>}
                  {selectedProspect.tier && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${tierColor(selectedProspect.tier)}`}>
                      Tier {selectedProspect.tier} — {selectedProspect.score} pts
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={closePanel}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Panel body - scrollable */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {/* Website */}
              {selectedProspect.website && (
                <a
                  href={selectedProspect.website.startsWith('http') ? selectedProspect.website : `https://${selectedProspect.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                >
                  <Globe className="w-4 h-4" />
                  {selectedProspect.website}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}

              {/* Phone - large */}
              <div className="bg-blue-50 rounded-lg p-4 flex items-center gap-3">
                <Phone className="w-6 h-6 text-blue-600" />
                {selectedProspect.phone ? (
                  <a
                    href={`tel:${selectedProspect.phone}`}
                    className="text-xl font-bold text-blue-700 hover:text-blue-900"
                  >
                    {selectedProspect.phone}
                  </a>
                ) : (
                  <span className="text-lg text-gray-400">Sin teléfono registrado</span>
                )}
              </div>

              {/* Questions guide */}
              {Array.isArray(selectedProspect.call_questions) && selectedProspect.call_questions.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-xs font-semibold text-amber-700 uppercase mb-2">Preguntas para la llamada</p>
                  <ol className="list-decimal list-inside space-y-1">
                    {selectedProspect.call_questions.map((q, i) => (
                      <li key={i} className="text-sm text-amber-900">{q}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Size info */}
              {selectedProspect.size_estimate && (
                <div className="text-sm text-gray-500">
                  <span className="font-medium text-gray-700">Tamaño estimado actual:</span>{' '}
                  {selectedProspect.size_estimate}
                </div>
              )}

              {/* Call history */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-3">
                  Historial de llamadas
                </h3>
                {drawerCallsLoading ? (
                  <div className="text-sm text-gray-400 flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Cargando llamadas...
                  </div>
                ) : drawerCalls.length === 0 ? (
                  <p className="text-sm text-gray-400">
                    Aún no hay llamadas registradas para este lead.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {drawerCalls.map((c) => {
                      const startedDate = c.started_at
                        ? new Date(c.started_at).toLocaleString('es-ES')
                        : '—';
                      const DirIcon = c.direction === 'inbound' ? PhoneIncoming : PhoneOutgoing;
                      return (
                        <div key={c.id} className="border border-gray-200 rounded-lg p-3 bg-white">
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <div className="flex items-center gap-1.5">
                              <DirIcon className="w-3.5 h-3.5 text-gray-500" />
                              <span>{c.direction === 'inbound' ? 'Entrante' : 'Saliente'}</span>
                              {c.commercial_name && (
                                <span className="text-gray-400">· {c.commercial_name}</span>
                              )}
                            </div>
                            <span>{startedDate}</span>
                          </div>
                          <div className="mt-1 flex items-center gap-3 text-xs text-gray-600">
                            <span>Duración: {formatCallDuration(c.duration_seconds)}</span>
                            {c.status && <span>Estado: {c.status}</span>}
                          </div>
                          {c.audio_url && (
                            <audio
                              controls
                              preload="none"
                              src={c.audio_url}
                              className="w-full mt-2 h-8"
                            />
                          )}
                          {c.ai_summary && (
                            <details className="mt-2 text-xs">
                              <summary className="cursor-pointer font-medium text-gray-700">
                                Resumen IA
                              </summary>
                              <p className="mt-1 text-gray-600 whitespace-pre-wrap">{c.ai_summary}</p>
                            </details>
                          )}
                          {c.ai_next_action && (
                            <details className="mt-1 text-xs">
                              <summary className="cursor-pointer font-medium text-gray-700">
                                Siguiente acción sugerida
                              </summary>
                              <p className="mt-1 text-gray-600 whitespace-pre-wrap">{c.ai_next_action}</p>
                            </details>
                          )}
                          {c.transcript && (
                            <details className="mt-1 text-xs">
                              <summary className="cursor-pointer font-medium text-gray-700">
                                Transcripción
                              </summary>
                              <p className="mt-1 text-gray-600 whitespace-pre-wrap">{c.transcript}</p>
                            </details>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* --- Form --- */}
              <div className="border-t border-gray-200 pt-4 space-y-4">
                <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider">Datos de la llamada</h3>

                {/* Size range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rango de empleados</label>
                  <select
                    value={formSizeRange}
                    onChange={(e) => setFormSizeRange(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">-- Seleccionar --</option>
                    {SIZE_RANGES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                  {(formSizeRange === '501-1000' || formSizeRange === '>1000') && (
                    <p className="mt-1 text-xs text-red-600 font-medium">
                      Se marcará como excluido (tamaño &gt;500 confirmado por llamada)
                    </p>
                  )}
                </div>

                {/* Size comment */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Comentario sobre tamaño <span className="text-gray-400 font-normal">(opcional)</span></label>
                  <input
                    type="text"
                    value={formSizeComment}
                    onChange={(e) => setFormSizeComment(e.target.value)}
                    placeholder="Ej: Confirman 120 empleados en plantilla"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Decision maker name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <User className="w-3.5 h-3.5 inline mr-1" />
                    Nombre del decisor
                  </label>
                  <input
                    type="text"
                    value={formDMName}
                    onChange={(e) => setFormDMName(e.target.value)}
                    placeholder="Ej: Juan Garcia Lopez"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Decision maker role */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cargo del decisor</label>
                  <input
                    type="text"
                    value={formDMRole}
                    onChange={(e) => setFormDMRole(e.target.value)}
                    placeholder="Ej: Director de Operaciones"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Decision maker email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email del decisor <span className="text-gray-400 font-normal">(opcional)</span></label>
                  <input
                    type="email"
                    value={formDMEmail}
                    onChange={(e) => setFormDMEmail(e.target.value)}
                    placeholder="Ej: jgarcia@empresa.com"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Call notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notas de la llamada</label>
                  <textarea
                    value={formCallNotes}
                    onChange={(e) => setFormCallNotes(e.target.value)}
                    rows={3}
                    placeholder="Apuntes de la conversacion..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Result */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resultado</label>
                  <select
                    value={formResult}
                    onChange={(e) => setFormResult(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">-- Seleccionar resultado --</option>
                    {RESULT_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                {/* Discard reason (conditional) */}
                {formResult === 'descartado' && (
                  <div>
                    <label className="block text-sm font-medium text-red-700 mb-1">Motivo de descarte *</label>
                    <input
                      type="text"
                      value={formDiscardReason}
                      onChange={(e) => setFormDiscardReason(e.target.value)}
                      placeholder="Ej: No tienen necesidad de software"
                      className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Panel footer - save */}
            <div className="border-t border-gray-200 px-5 py-4 bg-gray-50">
              <button
                onClick={handleSave}
                disabled={saving || !formResult || (formResult === 'descartado' && !formDiscardReason.trim())}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Guardar
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OutboundCallQueue;
